import React from 'react'
import type { PlayerPieceComponent, PlayerPieceProps } from '@merels/sdk'

interface SafePlayerPieceProps extends PlayerPieceProps {
  component: PlayerPieceComponent
}

interface SafePlayerPieceBoundaryProps {
  component: PlayerPieceComponent
  children: React.ReactNode
}

interface SafePlayerPieceBoundaryState {
  hasError: boolean
}

/**
 * Prevents a game-provided player glyph from taking down the platform chrome.
 * The game board has its own boundary, but player pieces also render in the
 * header and mate list outside that boundary.
 */
class SafePlayerPieceBoundary extends React.Component<
  SafePlayerPieceBoundaryProps,
  SafePlayerPieceBoundaryState
> {
  constructor(props: SafePlayerPieceBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[SafePlayerPiece] Game-provided player piece crashed:', error, errorInfo)
  }

  componentDidUpdate(previousProps: SafePlayerPieceBoundaryProps) {
    if (this.props.component !== previousProps.component && this.state.hasError) {
      this.setState({ hasError: false })
    }
  }

  render() {
    return this.state.hasError ? null : this.props.children
  }
}

/** Renders a game-provided player glyph without allowing it to crash host UI. */
export function SafePlayerPiece({ component: Piece, ...props }: SafePlayerPieceProps) {
  return (
    <SafePlayerPieceBoundary component={Piece}>
      <Piece {...props} />
    </SafePlayerPieceBoundary>
  )
}
